import { AxiosInstance } from 'axios';

type CookieSizeInfo = {
  name: string;
  bytes: number;
};

export type CookieHeaderMeasurement = {
  totalBytes: number;
  cookies: CookieSizeInfo[];
};

export const COOKIE_SIZE_WARN_THRESHOLD_BYTES = 4096;

const MAX_COOKIES_IN_REPORT = 25;
const MIN_GROWTH_TO_REREPORT_BYTES = 512;
const SESSION_STORAGE_KEY = 'cast:cookie-size-watchdog-last-reported';

type CaptureWarning = (message: string, extra: Record<string, unknown>) => void;

export type CookieSizeWatchdogOptions = {
  captureWarning?: CaptureWarning;
  thresholdBytes?: number;
};

/**
 * Splits the JS-visible cookie jar into per-cookie wire sizes. Values in
 * `document.cookie` are already URL-decoded, so char length approximates the
 * header bytes for ASCII-dominant cookies — good enough for a watchdog.
 */
export const measureCookieHeader = (
  cookieString: string
): CookieHeaderMeasurement => ({
  totalBytes: cookieString.length,
  cookies: cookieString
    // some user agents omit the space after the semicolon
    .split(';')
    .map((entry) => entry.replace(/^\s+/, ''))
    .filter(Boolean)
    .map((entry) => ({
      name: entry.slice(0, entry.indexOf('=')),
      bytes: entry.length,
    })),
});

const readLastReportedTotal = (): number => {
  try {
    return Number(sessionStorage.getItem(SESSION_STORAGE_KEY)) || 0;
  } catch {
    // storage can be blocked for exactly the users this watchdog targets
    return 0;
  }
};

const persistLastReportedTotal = (totalBytes: number): void => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, String(totalBytes));
  } catch {
    // fall back to reporting once per page load via the in-memory value
  }
};

/**
 * Measures the outgoing Cookie header on every request and reports via
 * `captureWarning` when it approaches the typical server header limit.
 *
 * Reports at most once per size "step": again only when the header grew by
 * at least MIN_GROWTH_TO_REREPORT_BYTES since the last report. The last
 * reported size survives reloads via sessionStorage so users stuck in
 * cookie-related reload loops don't emit one message per reload.
 *
 * The interceptor never throws — a watchdog must not break the requests it
 * observes.
 */
export const attachCookieSizeWatchdog = (
  client: AxiosInstance,
  {
    captureWarning,
    thresholdBytes = COOKIE_SIZE_WARN_THRESHOLD_BYTES,
  }: CookieSizeWatchdogOptions
): void => {
  let lastReportedTotal = readLastReportedTotal();

  client.interceptors.request.use((requestConfig) => {
    try {
      if (!captureWarning || typeof document === 'undefined') {
        return requestConfig;
      }

      const { totalBytes, cookies } = measureCookieHeader(document.cookie);
      // first report fires at the threshold itself; every later report
      // requires visible growth since the previous one
      const hasNeverReported = lastReportedTotal === 0;
      const shouldReport =
        totalBytes >= thresholdBytes &&
        (hasNeverReported ||
          totalBytes - lastReportedTotal >= MIN_GROWTH_TO_REREPORT_BYTES);

      if (!shouldReport) {
        return requestConfig;
      }

      lastReportedTotal = totalBytes;
      persistLastReportedTotal(totalBytes);

      captureWarning('Cookie header approaching request header size limit', {
        cookieHeaderBytes: totalBytes,
        thresholdBytes,
        cookieCount: cookies.length,
        largestCookies: cookies
          .sort((a, b) => b.bytes - a.bytes)
          .slice(0, MAX_COOKIES_IN_REPORT),
        requestUrl: requestConfig.url,
        note:
          'HttpOnly cookies (e.g. cast-console-auth) are invisible to ' +
          'JS — the actual Cookie header sent by the browser is larger ' +
          'than reported here',
      });
    } catch {
      // a watchdog must never break the request it observes
    }

    return requestConfig;
  });
};
